/**
 * Swahili catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Native-grade Standard Swahili (Kiswahili) at a mathematician's register.
 * **Warrant → «Uthibitisho»** (justification/warrant — flagged). Where Swahili standardly
 * borrows a scientific term (logic, modal, heuristic, postulate) the loanword is used. W/M
 * grades stay Latin (`::numbering-system/latn`). CLDR cardinal categories are one / other, so
 * `frameActive` declares exactly those (plus an explicit `=0`); the noun uses the ki-/vi- noun
 * class (kizuizi → vizuizi). Per INV-STABLE-ID a wrong translation is a presentation concern
 * only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const SW_CATALOG = {
    locale: 'sw',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Uchunguzi',
        [P2.sectionClaims]: 'Madai',
        [P2.sectionIntegrity]: 'Uadilifu',
        [P2.axisWarrantLabel]: 'Uthibitisho',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Kimajaribio',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Ufafanuzi} interp {Tafsiri} testable {Inajaribika} tested {Imejaribiwa} refuted {Imekanushwa} program {Mpango wa utafiti} philos {Kifalsafa} other {Haijulikani}}',
        [P2.axisModalLabel]: 'Modali',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Kiwango cha nguvu {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Inachunguzika',
        [P2.stateRefuted]: 'Imekanushwa',
        [P2.stateOutside]: 'Nje ya wigo',
        [P2.verdictConflict]: 'Inakinzana na {constraints}',
        [P2.verdictOutside]: 'Inahitaji {missing}',
        [P2.lossRegisterPlain]: 'Imerahisishwa kwa lugha ya kawaida — baadhi ya usahihi umeachwa.',
        [P2.lossRegisterKid]: 'Imerahisishwa kwa wasomaji wachanga — maelezo mengi yameachwa.',
        [P2.lossProfileDetail]: 'Wasifu kamili wa Uthibitisho / Kimajaribio / Modali umefupishwa hapa.',
        [P2.lossConflictDetail]: 'Maelezo ya mgongano yamefupishwa katika mwonekano huu.',
        [P2.laneVerified]: 'Imethibitishwa',
        [P2.laneUnverified]: 'Haijathibitishwa',
        [P2.laneConflict]: 'Mgongano umethibitishwa',
        [P2.constraintLogic]: 'Mantiki',
        [P2.constraintLightBends]: 'Mwanga hupinda katika uga wa uvutano',
        [P2.constraintParallelPostulate]: 'Postulati ya mistari sambamba',
        [P2.claimScalarGravity]: 'Nadharia ya skala ya uvutano',
        [P2.claimNonEuclidean]: 'Jiometri isiyo ya Euclid',
        [P2.claimSubstrateSr]: 'Tafsiri ya substrati ya uhusiano maalum',
        [P2.edgeExtObservation]: 'Uchunguzi wa nje',
        [P2.edgeResourceSample]: 'Rasilimali ya kukokotoa inayoshirikiwa',
        [P2.edgeHeuristicSample]: 'Mwongozo wa kiheuristiki',
        [P2.edgeSharedSample]: 'Msingi unaoshirikiwa',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Vizuizi',
        [P2.sectionEdges]: 'Kingo',
        [P2.sectionFrame]: 'Fremu',
        [P2.sectionExposure]: 'Kuathirika',
        [P2.statusAdoptedFormal]: 'Imekubaliwa (rasmi)',
        [P2.statusAdoptedInterpretive]: 'Imekubaliwa (kitafsiri)',
        [P2.statusSuspended]: 'Imesimamishwa',
        [P2.edgeEffectConstraint]: 'Huunda kizuizi (huweza kukanusha)',
        [P2.edgeEffectResource]: 'Rasilimali — hakuna athari kwa hukumu',
        [P2.edgeEffectHeuristic]: 'Kiheuristiki — hakuna athari kwa hukumu',
        [P2.edgeEffectShared]: 'Msingi unaoshirikiwa — hakuna athari kwa hukumu',
        [P2.frameActive]: '{count, plural, =0 {Hakuna vizuizi amilifu} one {# kizuizi amilifu} other {# vizuizi amilifu}}',
        [P2.exposureNone]: 'Hakuna utegemezi wa kitafsiri',
        [P2.exposureDep]: 'Inategemea {id}',
        [P2.laneTrackMachine]: 'Mashine',
        [P2.laneTrackExpert]: 'Mtaalamu',
        [P2.laneTrackCommunity]: 'Jamii',
        [P2.expertAudited]: 'Imekaguliwa',
        [P2.expertInProgress]: 'Inaendelea',
        [P2.expertNotReviewed]: 'Haijapitiwa',
        [P2.communityFlags]: 'Kuna alama',
        [P2.communityNone]: 'Hakuna alama',
        [P2.controlChallenge]: 'Pinga',
        [P2.controlActivate]: 'Washa',
        [P2.controlMakeInterpretive]: 'Fanya kitafsiri',
        [P2.controlImportEdge]: 'Ingiza kingo',
        [P2.controlPromote]: 'Pandisha',
        [P2.controlUndo]: 'Tendua',
        [P2.controlRedo]: 'Rudia',
        [P2.controlRegister]: 'Sajili',
        [P2.promoteRejected]: 'Upandishaji umekataliwa',
        [P2.promoteAccepted]: 'Upandishaji umekubaliwa',
        [P2.promoteReasonRefutationTerminal]: 'Uthibitisho hauwezi kuponya ukanushaji.',
        [P2.promoteReasonKeystone]: 'Upandishaji muhimu unahitaji uungwaji mkono wa kimajaribio.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Aina ya kimajaribio haijawekwa.',
        [P2.promoteReasonModalNotFixed]: 'Kiwango cha modali hakijawekwa.',
        [P2.promoteReasonWarrantNotImproved]: 'Kiwango cha uthibitisho hakijaboreshwa.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=sw.js.map